import { theme } from 'lib';
import styled from 'styled-components';

/**
 * CSS-grid stand-ins for `TableComponents`' real `<table>`, used only by the TIRI list.
 *
 * Two reasons this list can't stay on the shared table:
 *
 * 1. That table is `table-layout: auto`, so the browser re-measures every cell in every row to
 *    recompute column widths. With an infinite list that makes each 50-row append cost O(total
 *    rows) — the append work climbs the further you page, which is the lag being fixed here.
 *    A fixed grid template sizes columns from the template alone, so appending is O(new rows).
 * 2. Rows have to be virtualized, and virtualization needs to position rows itself. Size
 *    containment is spec'd not to apply to `table-row`/`table-cell`, and a `<tr>` can't be
 *    absolutely positioned out of its table's layout, so real table rows can't be windowed.
 *
 * `TableComponents` is shared by ~20 admin screens and is deliberately left untouched. These
 * are sized and coloured to match it, so the TIRI list looks the same as before.
 */

// One template drives both the header and every row, so the two cannot drift out of alignment.
// Order matches the `Header` in ./List.js: Media, Modules, Source, Uploader, Team, Status,
// Category, In feed, Uploaded At. No `auto` tracks — a content-sized track would reintroduce
// exactly the per-append re-measuring this exists to avoid.
export const COLUMNS = '88px 150px 130px 1.4fr 1.1fr 110px 1.2fr 90px 190px';

// The shared `Tr` is a hard 53px, and matching it exactly is what lets the virtualizer size the
// scroll area from a count instead of measuring rows. Any change here must move with it.
export const ROW_HEIGHT = 53;

// Matches TableComponents' `first`/`last` Td padding, which the grid can apply once on the
// track container instead of per edge cell.
const EDGE_PAD = 24;

export const GridTable = styled.div`
  width: 100%;
`;

export const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  padding: 0 ${EDGE_PAD}px;
  /* Sticks to the scroll container the virtualizer reads, so it stays put while rows recycle
     underneath it. Outside the virtualized area on purpose — it must never be windowed away. */
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  border-bottom: 1px solid #000;
  min-height: 45px;
`;

export const HeaderCell = styled.div`
  padding: 12px 8px;
  /* Centers raw/inline content. The Text component is NOT raw content — it declares its own
     text-align, so a heading rendered through it must pass center itself (see ./List.js). */
  text-align: center;
`;

/**
 * The virtualizer's containing block. Its height is the full virtual height of every loaded
 * row, so the scrollbar and the infinite-scroll sentinel below it both behave as if all the
 * rows were really there.
 */
export const Viewport = styled.div`
  position: relative;
  width: 100%;
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  padding: 0 ${EDGE_PAD}px;
  height: ${ROW_HEIGHT}px;
  border-bottom: 1px solid #ddd;
  cursor: pointer;
  background-color: ${(props) => (props.isSelected ? theme.GREY_10 : 'transparent')};

  &:hover {
    background-color: ${theme.GREY_10};
  }
`;

export const Cell = styled.div`
  padding: 8px;
  text-align: center;
  /* Fixed tracks mean over-long content overflows rather than widening the column, so cells
     clip. The Text elements inside carry the ellipsis. */
  overflow: hidden;
`;
